import { useEffect } from "react";

interface SeoOptions {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function buildAbsolute(path: string): string {
  const base = `${window.location.origin}${__BASE_PATH__ || "/"}`.replace(/\/+$/, "");
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean === "/" ? "" : clean}` || base;
}

export function useSeo(options: SeoOptions): void {
  const { title, description, keywords, canonicalPath, ogType = "website", jsonLd } = options;

  useEffect(() => {
    const url = buildAbsolute(canonicalPath);
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow, max-image-preview:large");
    upsertMeta("name", "last-modified", new Date().toISOString().slice(0, 10));
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", "黑料网爆");
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    const prevScript = document.getElementById("seo-jsonld");
    if (prevScript) {
      prevScript.remove();
    }
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-jsonld";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);
}